import { DestroyRef, Injectable, inject, signal } from '@angular/core';

import { MapLayersService } from './map-layers.service.js';
import { PointCloudApiService } from './pointcloud-api.service.js';
import { VectorTileBatchService } from './vector-tile-batch.service.js';
import { ViewerCoreService } from './viewer-core.service.js';
import type { VectorTileLayerConfig } from '../types/vector-tile-layer.js';

@Injectable({ providedIn: 'root' })
export class TerrainService {
  private core = inject(ViewerCoreService);
  private destroyRef = inject(DestroyRef);
  private pointCloudApi = inject(PointCloudApiService);
  private mapLayers = inject(MapLayersService);
  private vectorTileBatch = inject(VectorTileBatchService);

  // ─── Reactive state ─────────────────────────────────────────────────────────

  readonly terrainEnabled = signal(false);
  readonly terrainLoaded = signal(false);
  readonly quantizedTerrainEnabled = signal(false);
  readonly quantizedTerrainLoaded = signal(false);
  readonly textureLayer = signal<'map' | 'aerial' | null>(null);
  readonly featureLayers = signal<string[]>([]);

  // Digital twin models overlay
  readonly digitalTwinVisible = signal(false);
  readonly digitalTwinLoading = signal(false);
  private digitalTwinLoaded = false;

  // ─── 3D Tiles Terrain ─────────────────────────────────────────────────────

  async loadSwissTerrain(): Promise<void> {
    const viewer = this.core.viewer();
    if (!viewer) {
      throw new Error('Viewer not initialized');
    }
    await viewer.loadSwissTerrain();
    this.terrainLoaded.set(true);
    this.terrainEnabled.set(true);
  }

  setTerrainEnabled(enabled: boolean): void {
    const viewer = this.core.viewer();
    if (viewer?.tilesManager) {
      viewer.setTerrainEnabled(enabled);
      this.terrainEnabled.set(enabled);
    }
  }

  toggleTerrain(): void {
    const viewer = this.core.viewer();
    if (viewer?.tilesManager) {
      viewer.toggleTerrain();
      this.terrainEnabled.set(viewer.isTerrainEnabled());
    }
  }

  // ─── Quantized Mesh Terrain ─────────────────────────────────────────────────

  async loadQuantizedMeshTerrain(layer: 'map' | 'aerial' = 'map'): Promise<void> {
    const viewer = this.core.viewer();
    if (!viewer) {
      throw new Error('Viewer not initialized');
    }
    await viewer.loadQuantizedMeshTerrain(layer);
    this.quantizedTerrainLoaded.set(true);
    this.quantizedTerrainEnabled.set(true);
    this.textureLayer.set(layer);
  }

  async setQuantizedTerrainTextureLayer(layer: 'map' | 'aerial'): Promise<void> {
    const viewer = this.core.viewer();
    if (viewer?.quantizedTerrainManager) {
      await viewer.quantizedTerrainManager.setTextureLayer(layer);
      this.textureLayer.set(layer);
    }
  }

  setQuantizedTerrainEnabled(enabled: boolean): void {
    const viewer = this.core.viewer();
    if (viewer?.quantizedTerrainManager) {
      viewer.setQuantizedTerrainEnabled(enabled);
      this.quantizedTerrainEnabled.set(enabled);
    }
  }

  toggleQuantizedTerrain(): void {
    const viewer = this.core.viewer();
    if (viewer?.quantizedTerrainManager) {
      viewer.toggleQuantizedTerrain();
      this.quantizedTerrainEnabled.set(viewer.isQuantizedTerrainEnabled());
    }
  }

  // ─── Digital Twin Models overlay ────────────────────────────────────────────

  /**
   * Fetch all digital twin models for the current tenant and render them as
   * GeoJSON point markers on the quantized mesh terrain.
   */
  async loadDigitalTwinModels(): Promise<void> {
    const manager = this.core.viewer()?.quantizedTerrainManager;
    if (!manager) return;

    // Already fetched — just make the existing markers visible again.
    if (this.digitalTwinLoaded) {
      manager.setFeaturesVisible(true);
      this.digitalTwinVisible.set(true);
      return;
    }

    const tenant = new URLSearchParams(window.location.search).get('tenant') ?? '';

    this.digitalTwinLoading.set(true);
    try {
      const features = await this.pointCloudApi.fetchDigitalTwinFeatures(tenant);
      await manager.setDigitalTwinFeatures(features);
      manager.setFeaturesVisible(true);
      this.digitalTwinLoaded = true;
      this.digitalTwinVisible.set(true);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        this.digitalTwinVisible.set(false);
        return;
      }
      console.error('Error loading digital twin models:', error);
      this.digitalTwinVisible.set(false);
      throw error;
    } finally {
      this.digitalTwinLoading.set(false);
    }
  }

  setDigitalTwinModelsVisible(visible: boolean): void {
    const manager = this.core.viewer()?.quantizedTerrainManager;
    manager?.setFeaturesVisible(visible);
    this.digitalTwinVisible.set(visible);
  }

  // ─── Internal vector tile layers ────────────────────────────────────────────

  /** Vector tile layers the backend offers, once the catalog has loaded. */
  readonly vectorTileLayerCatalog = this.mapLayers.vectorTileLayers;

  /** Fetch the map layer catalog. Cheap to call repeatedly; fetched once. */
  async loadVectorTileLayerCatalog(): Promise<VectorTileLayerConfig[]> {
    return this.mapLayers.loadCatalog();
  }

  /**
   * Enable or disable draping an internal vector tile layer onto the terrain.
   * Features are fetched in batches via {@link VectorTileBatchService} and
   * decoded/draped by the quantized mesh terrain manager.
   */
  toggleVectorTileLayer(layerId: string): void {
    const manager = this.core.viewer()?.quantizedTerrainManager;
    const config = this.mapLayers.vectorTileLayers().find(layer => layer.id === layerId);
    if (!manager || !config) return;

    const enabled = !this.featureLayers().includes(layerId);
    manager.setVectorTileLayerEnabled(layerId, enabled, {
      uri: config.uri,
      maxZoom: config.maxZoom,
      color: config.color,
      requestTile: (layer, z, x, y) => this.vectorTileBatch.requestTile(layer, z, x, y),
    });

    this.featureLayers.update(ids =>
      enabled ? [...ids, layerId] : ids.filter(id => id !== layerId),
    );
  }

  /** Disable every currently active vector tile layer, e.g. when terrain is turned off. */
  disableAllVectorTileLayers(): void {
    const manager = this.core.viewer()?.quantizedTerrainManager;
    if (manager) {
      for (const layerId of this.featureLayers()) {
        manager.setVectorTileLayerEnabled(layerId, false);
      }
    }
    this.featureLayers.set([]);
  }
}
